import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { useRouter } from 'next/router'
import numeral from 'numeral'
import { useMemo } from 'react'

import { Table } from 'src/components/molecules/Table'
import { MonitoringLine } from 'src/components/organisms/AdminMonitoringEvent/AdminMonitoringEvent'

import {
  adminMonitoringEventPath,
  viewMonitoringEventPath
} from 'src/config/paths'
import { ListMonitoringType } from 'src/types/monitoring'
import { formatDate } from 'src/utils/date'

export interface MonitoringsTableProps {
  monitoringsData: ListMonitoringType[]
  applicationId: string
  plantingId: string
  plantingQuantity: number
  isAdmin: boolean
  projectId: string
}

export const MonitoringsTable = ({
  monitoringsData,
  applicationId,
  projectId,
  plantingId,
  plantingQuantity,
  isAdmin
}: MonitoringsTableProps) => {
  const router = useRouter()
  const monitoringEventPath = isAdmin
    ? adminMonitoringEventPath
    : viewMonitoringEventPath
  const handleRowSelect = (row: ListMonitoringType) => {
    router.push(
      monitoringEventPath({
        routeParams: {
          applicationId,
          plantingId,
          eventId: row.Id,
          projectId
        }
      })
    )
  }

  const columnHelper = createColumnHelper<ListMonitoringType>()

  const columns: ColumnDef<ListMonitoringType, any>[] = useMemo(() => {
    return [
      columnHelper.accessor('MonitoredOn', {
        header: 'Monitoring date',
        cell: props =>
          props.getValue()
            ? formatDate({
                date: props.getValue(),
                format: 'DD/MM/YYYY',
                isUtc: true
              })
            : '-'
      }),
      columnHelper.accessor('MonitoringLines', {
        header: 'Estimated surviving trees',
        cell: props => {
          const monitoringLines = props.getValue() as MonitoringLine[]
          // this performs a weighted average rather than a simple average
          // ie. 50% of 1000 and 100% of 10 would be 50.4% not 75%
          const survivingTrees = monitoringLines.reduce(
            (acc, val) => acc + val.SurvivingTrees,
            0
          )

          return (
            <span>
              {numeral((survivingTrees / plantingQuantity) * 100).format('0,0')}
              %
            </span>
          )
        }
      }),
      columnHelper.accessor('CreatedOn', {
        header: 'Created',
        cell: props =>
          props.getValue()
            ? formatDate({
                date: props.getValue(),
                format: 'DD/MM/YYYY',
                isUtc: true
              })
            : '-'
      })
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitoringsData])

  return (
    <Table
      columns={columns}
      data={monitoringsData}
      onSelect={handleRowSelect}
    />
  )
}
